import { useCallback, useMemo, useState } from 'react';

import PortalIdParser from 'PortalIdParser';

import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import useUserInfo from 'framework-listing-lib/internal/hooks/useUserInfo';
import { useViewProps as useViewPropsContext } from 'framework-listing-lib/internal/providers/ListingProvider';
import {
  ChangeViewOptions,
  ViewUpdateState,
} from 'framework-listing-lib/types';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';

import { useCurrentViewContext } from 'framework-listing-lib/internal/ViewTabs/providers/CurrentViewProvider';
import { useViews as useViewsProvider } from 'framework-listing-lib/internal/ViewTabs/providers/ViewsProvider';
import { getViewId } from 'framework-listing-lib/internal/ViewTabs/utils/views';

const DEFAULT_SAVE_AS_NEW_PROPS = {
  disabled: false,
};

/**
 * façade over the context hook in case implementation changes
 */
function useViewProps() {
  'use memo';
  const viewProps = useViewPropsContext();

  return viewProps;
}

export function useHasViews() {
  'use memo';
  const viewProps = useViewProps();

  return useMemo(() => Boolean(viewProps && viewProps.hasViews), [viewProps]);
}

export function useOnViewIdChange() {
  'use memo';
  const viewProps = useViewProps();

  return useCallback(
    (viewId: string, options?: ChangeViewOptions) => {
      if (viewProps && viewProps.onViewIdChange) {
        viewProps.onViewIdChange(viewId, options);
      }

      listingLibObserver.setBulkActionSelectionMode('none');
    },
    [viewProps]
  );
}

export function useViewsNamespace() {
  'use memo';
  const viewProps = useViewProps();

  return useMemo(
    () => (viewProps && viewProps.namespace ? viewProps.namespace : 'NONE'),
    [viewProps]
  );
}

export function useGetSaveAsNewViewProps() {
  'use memo';
  const viewProps = useViewProps();
  const currentView = useCurrentView();

  return useMemo(() => {
    if (!currentView || !viewProps || !viewProps.getSaveAsNewViewProps) {
      return DEFAULT_SAVE_AS_NEW_PROPS;
    }

    return viewProps.getSaveAsNewViewProps(currentView);
  }, [currentView, viewProps]);
}

export function useRenderTab() {
  'use memo';
  const viewProps = useViewProps();

  return useMemo(
    () => (viewProps && viewProps.renderTab ? viewProps.renderTab : undefined),
    [viewProps]
  );
}

export function useFiltersForNewViews() {
  'use memo';
  const viewProps = useViewProps();

  return useMemo(
    () => ({
      filterGroups: [],
      quickFilters: [],
      ...(viewProps && viewProps.filtersForNewViews
        ? viewProps.filtersForNewViews || {}
        : {}),
    }),
    [viewProps]
  );
}

export function useViewPropsCurrentViewId() {
  'use memo';
  const viewProps = useViewProps();

  return useMemo(
    () =>
      viewProps && viewProps.currentViewId
        ? viewProps.currentViewId
        : undefined,
    [viewProps]
  );
}

export function useCurrentViewId() {
  'use memo';
  const isFoldersMode = useIsFoldersMode();

  const { currentView } = useCurrentViewContext();

  if (isFoldersMode) {
    return undefined;
  }

  return currentView ? getViewId(currentView) : undefined;
}

export function useCurrentView() {
  'use memo';
  const isFoldersMode = useIsFoldersMode();

  const { currentView } = useCurrentViewContext();

  if (isFoldersMode) {
    return undefined;
  }

  return currentView;
}

export function useIsWaitingForFirstCurrentView() {
  'use memo';
  const hasViews = useHasViews();
  const isFoldersMode = useIsFoldersMode();
  const { currentView } = useCurrentViewContext();
  const [hasHadCurrentView, setHasHadCurrentView] = useState(false);

  if (currentView && !hasHadCurrentView) {
    setHasHadCurrentView(true);
  }

  return hasViews && !isFoldersMode && !hasHadCurrentView && !currentView;
}

export function useIsCurrentViewReadOnly() {
  'use memo';
  const currentView = useCurrentView();

  const { userInfo } = useUserInfo();

  return Boolean(
    userInfo && currentView && userInfo.user.user_id !== currentView.ownerId
  );
}

export function useCurrentViewFilters() {
  'use memo';
  const currentView = useCurrentView();

  return useMemo(() => {
    if (currentView) {
      return {
        filterGroups: currentView.filterGroups,
        quickFilters: currentView.quickFilters || [],
      };
    }

    return {
      filterGroups: [],
      quickFilters: [],
    };
  }, [currentView]);
}

export function useViewUpdateState() {
  'use memo';
  const { viewUpdateState, setViewUpdateState } = useViewsProvider();

  return [viewUpdateState, setViewUpdateState] as [
    ViewUpdateState,
    typeof setViewUpdateState,
  ];
}

export function useViewColumns() {
  'use memo';
  const { columns } = useViewsProvider();

  return columns;
}

export function useSetViewColumns() {
  'use memo';
  const { setColumns } = useViewsProvider();

  return setColumns;
}

export function useNumberOfFrozenColumns() {
  'use memo';
  const { numberOfFrozenColumns } = useViewsProvider();

  return useMemo(() => numberOfFrozenColumns, [numberOfFrozenColumns]);
}

export function useSetNumberOfFrozenColumns() {
  'use memo';
  const { setNumberOfFrozenColumns } = useViewsProvider();

  return setNumberOfFrozenColumns;
}

export function useSetCardProperties() {
  'use memo';
  const { setCardProperties } = useViewsProvider();

  return setCardProperties;
}

export function useCardProperties() {
  'use memo';
  const { cardProperties } = useViewsProvider();

  return cardProperties;
}

export function useQuickFilterPropertyNamesState() {
  'use memo';
  const { quickFilterPropertyNames } = useViewsProvider();

  return quickFilterPropertyNames;
}

export function useGetAllViewsUrl() {
  'use memo';
  const viewProps = useViewProps();

  const objectType = useObjectTypeId();
  const { userInfo } = useUserInfo();
  const namespace = useViewsNamespace();

  const urlSearchParams = useMemo(() => {
    const draftParms = new URLSearchParams();
    draftParms.append('namespace', namespace);
    draftParms.append('objectTypeId', objectType);
    draftParms.append(
      'returnLink',
      `${window.location.pathname}${window.location.search}`
    );

    if (userInfo?.user) {
      draftParms.append('ownerId', `${userInfo.user.user_id}`);
    }

    return draftParms;
  }, [namespace, objectType, userInfo]);

  return useMemo(
    () =>
      viewProps && viewProps.allViewsUrl
        ? `${viewProps.allViewsUrl}?${urlSearchParams.toString()}`
        : `/manage-views/${PortalIdParser.get()}/all?${urlSearchParams.toString()}`,
    [urlSearchParams, viewProps]
  );
}
